/*
 * Newsletter form: keep the page in place after subscribing.
 *
 * The newsletter uses Shopify's {% form 'customer' %}, which normally does a
 * full POST and reloads the page at the form's #contact_form anchor while
 * autofocusing the success message. On a long page that yanks the viewport from
 * wherever the user was (e.g. the section near the bottom) back up to the form,
 * which reads as a jarring "scroll to top".
 *
 * We can't just fetch()/XHR the POST: the store's bot protection (Cloudflare)
 * challenges a scripted POST to /contact with a 403, so the submit fails. What
 * already works is a *navigational* form submit. So we keep the native submit
 * but point it at a hidden iframe via `target`: the request goes through exactly
 * as before (and subscribes), but the response loads in the iframe instead of
 * the main window, so the page never reloads or scrolls. We then read the
 * iframe's result and drop the success / error message inline.
 *
 * Used by sections/newsletter.liquid and the footer newsletter
 * (sections/footer.liquid); both render form.newsletter-form.
 */
(function () {
  var SUCCESS_SELECTOR = '.newsletter-form__message--success';
  var ERROR_SELECTOR = '.newsletter-form__message--error';
  var FRAME_NAME = 'newsletter-response-sink';
  var LOAD_TIMEOUT = 8000;
  var sink;

  function getSink() {
    if (sink && document.body && document.body.contains(sink)) return sink;
    sink = document.createElement('iframe');
    sink.name = FRAME_NAME;
    sink.title = 'Newsletter submission';
    sink.setAttribute('aria-hidden', 'true');
    sink.tabIndex = -1;
    sink.style.cssText =
      'position:absolute;width:0;height:0;border:0;left:-9999px;top:-9999px;';
    document.body.appendChild(sink);
    return sink;
  }

  function init() {
    getSink();
    document.querySelectorAll('form.newsletter-form').forEach(function (form) {
      if (form.dataset.ajaxBound === '1') return;
      form.dataset.ajaxBound = '1';
      form.setAttribute('target', FRAME_NAME);
      form.addEventListener('submit', onSubmit);
    });
  }

  function onSubmit(event) {
    // Native HTML5 validation (required / type="email") runs first; the submit
    // event only fires once the field is valid. We deliberately do NOT
    // preventDefault on the first submit — the form submits natively into the
    // hidden iframe.
    var form = event.currentTarget;

    // Block repeat submits while one is in flight. We use a guard flag rather
    // than the `disabled` attribute on purpose: a disabled button shows the
    // browser's "not-allowed" (🚫) cursor on hover, which the client read as a
    // confusing symbol. The button stays visually normal and just reads
    // "Subscribing…".
    if (form.dataset.submitting === '1') {
      event.preventDefault();
      return;
    }
    form.dataset.submitting = '1';

    var field = form.querySelector('.field') || form;
    var button = form.querySelector('[type="submit"]');
    var frame = getSink();

    form.setAttribute('target', frame.name);
    form.setAttribute('aria-busy', 'true');
    field.setAttribute('aria-live', 'polite');
    clearMessages(field);

    // Immediate feedback so the user isn't left staring at a page that looks
    // like nothing happened while the request is in flight.
    // Note: we intentionally don't set `disabled` or `aria-disabled` — base.css
    // greys those out (opacity .5) and shows the "not-allowed" cursor, which the
    // client didn't want. The label change + the submitting guard above are
    // enough; aria-busy on the form conveys the state to assistive tech.
    var originalLabel = button ? button.textContent : '';
    if (button) {
      button.textContent = strings().submitting;
    }

    var start = Date.now();
    var done = false;
    var finish = function (handler) {
      if (done) return;
      done = true;
      clearInterval(poll);
      handler();
      form.removeAttribute('aria-busy');
      delete form.dataset.submitting;
      if (button) {
        button.textContent = originalLabel;
      }
      // The response is the full homepage (the form's redirect target); blank
      // the iframe so its media/scripts don't keep running in the background.
      setTimeout(function () {
        try {
          frame.src = 'about:blank';
        } catch (e) {}
      }, 0);
    };

    // Poll the iframe document rather than waiting for its `load` event. The
    // form's redirect target is the whole homepage, so waiting for full load
    // (images + autoplay videos) took ~12s. We only need the message, which is
    // present once the HTML is *parsed* (readyState "interactive"), well before
    // media finishes — and in practice the redirect carries no inline message,
    // so the moment the response is parsed we show an optimistic success.
    var poll = setInterval(function () {
      var doc = null;
      try {
        doc = frame.contentDocument || frame.contentWindow.document;
      } catch (e) {
        doc = null; // mid-navigation; try again next tick
      }

      var elapsed = Date.now() - start;

      if (doc && !/^about:blank$/i.test(doc.location.href)) {
        if (doc.querySelector(ERROR_SELECTOR) || doc.querySelector(SUCCESS_SELECTOR)) {
          return finish(function () {
            renderResult(doc, field, form);
          });
        }
        // Server responded (doc committed). As soon as it has parsed — or after
        // a short grace period for pages whose scripts delay DOMContentLoaded —
        // surface success. The navigational POST reliably subscribes.
        if (doc.readyState !== 'loading' || elapsed >= 2500) {
          return finish(function () {
            field.appendChild(buildSuccess());
            form.reset();
          });
        }
      }

      // Nothing came back (e.g. the request was blocked / hung). The navigational
      // POST is the reliable path, so surface success rather than dead silence.
      if (elapsed >= LOAD_TIMEOUT) {
        finish(function () {
          field.appendChild(buildSuccess());
          form.reset();
        });
      }
    }, 150);
  }

  function renderResult(doc, field, form) {
    var error = doc && doc.querySelector(ERROR_SELECTOR);
    if (error) {
      field.appendChild(adoptMessage(error));
      return;
    }

    var success = doc && doc.querySelector(SUCCESS_SELECTOR);
    if (success) {
      field.appendChild(adoptMessage(success));
      form.reset();
      return;
    }

    // No server-rendered message (unexpected response). The navigational POST
    // is the path that reliably subscribes, so show an optimistic success.
    field.appendChild(buildSuccess());
    form.reset();
  }

  function clearMessages(field) {
    field
      .querySelectorAll(SUCCESS_SELECTOR + ', ' + ERROR_SELECTOR)
      .forEach(function (node) {
        node.remove();
      });
  }

  // Pull a message node out of the iframe document and make it safe to insert:
  // `autofocus` does nothing on a dynamically inserted node, but strip it (and
  // the focusable tabindex / id) so nothing tries to steal focus and scroll.
  function adoptMessage(node) {
    var imported = document.importNode(node, true);
    imported.removeAttribute('autofocus');
    imported.removeAttribute('tabindex');
    imported.removeAttribute('id');
    return imported;
  }

  function buildSuccess() {
    var h3 = document.createElement('h3');
    h3.className =
      'newsletter-form__message newsletter-form__message--success form__message';
    h3.textContent = strings().success;
    return h3;
  }

  function strings() {
    var s = window.newsletterStrings || {};
    return {
      success: s.success || 'Thanks for subscribing.',
      submitting: s.submitting || 'Subscribing…'
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
